import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { fetchGaps } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Tag } from "@/components/Tag";
import { fmtDate } from "@/lib/format";
import { AlertCircle, ArrowRight } from "lucide-react";

export default function GapsPage() {
  const [gaps, setGaps] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const g = await fetchGaps();
    setGaps(g);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const handler = () => load();
    window.addEventListener("indielaw:sync-complete", handler);
    return () => window.removeEventListener("indielaw:sync-complete", handler);
  }, [load]);

  return (
    <div data-testid="gaps-page">
      <PageHeader
        eyebrow="Weekly review"
        title="Gaps"
        subtitle="Active beliefs that still have zero linked stories. Write a story for each, and they leave this list."
        testId="gaps-page-header"
      />

      <div
        data-testid="gaps-summary"
        className="mb-8 border border-[#141B4D]/15 bg-white rounded-sm p-5 flex items-start gap-4"
      >
        <div className="bg-[#FFD100] text-[#141B4D] p-2 rounded-sm">
          <AlertCircle size={18} strokeWidth={2.5} />
        </div>
        <div>
          <div className="text-2xl font-bold text-[#141B4D]" data-testid="gaps-count">
            {loading ? "—" : gaps.length}
          </div>
          <div className="text-xs uppercase tracking-wider text-[#141B4D]/60 mt-1">
            Beliefs still need stories
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b-2 border-[#141B4D]">
              <th className="py-3 pr-4 text-xs uppercase tracking-wider font-bold text-[#141B4D] w-[60%]">
                Belief
              </th>
              <th className="py-3 px-3 text-xs uppercase tracking-wider font-bold text-[#141B4D]">
                Category
              </th>
              <th className="py-3 px-3 text-xs uppercase tracking-wider font-bold text-[#141B4D]">
                Offer Tags
              </th>
              <th className="py-3 px-3 text-xs uppercase tracking-wider font-bold text-[#141B4D]">
                Added
              </th>
              <th className="py-3 pl-3 text-xs uppercase tracking-wider font-bold text-[#141B4D] w-[1%]"></th>
            </tr>
          </thead>
          <tbody>
            {gaps.map((b) => (
              <tr
                key={b.id}
                data-testid={`gap-row-${b.id}`}
                className="border-b border-[#141B4D]/15 hover:bg-[#141B4D]/5 transition-colors"
              >
                <td className="py-4 pr-4 align-top">
                  <Link
                    to={`/beliefs/${b.id}`}
                    data-testid={`gap-link-${b.id}`}
                    className="text-sm text-[#141B4D] font-medium leading-relaxed hover:underline decoration-[#FFD100] decoration-2 underline-offset-4"
                  >
                    {b.belief_text}
                  </Link>
                  {b.source && (
                    <div className="text-xs text-[#141B4D]/50 mt-1.5">
                      {b.source}
                    </div>
                  )}
                </td>
                <td className="py-4 px-3 align-top text-xs">{b.category || "—"}</td>
                <td className="py-4 px-3 align-top">
                  <div className="flex flex-wrap gap-1.5">
                    {(b.offer_tags || []).map((t) => (
                      <Tag key={t}>{t}</Tag>
                    ))}
                  </div>
                </td>
                <td className="py-4 px-3 align-top text-xs text-[#141B4D]/60 whitespace-nowrap">
                  {fmtDate(b.date_added)}
                </td>
                <td className="py-4 pl-3 align-top">
                  <Link
                    to={`/beliefs/${b.id}`}
                    data-testid={`gap-open-${b.id}`}
                    className="p-1.5 text-[#141B4D]/60 hover:text-[#141B4D] hover:bg-[#141B4D]/5 rounded-sm inline-flex"
                  >
                    <ArrowRight size={14} />
                  </Link>
                </td>
              </tr>
            ))}
            {!loading && gaps.length === 0 && (
              <tr>
                <td colSpan={5} className="py-12 text-center text-sm text-[#141B4D]/50">
                  No gaps. Every active belief has a story.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
